import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button, Input, Modal, Table, Card, useToast } from '../components/common';
import api from '../services/api';
import type { RequestLog } from '../types';

// 後端已處理錯誤訊息過濾，前端直接顯示即可

interface LogDetailData extends Omit<RequestLog, 'response_content'> {
  response_content?: string | null;
}

interface UserSuggestion {
  id: number;
  username: string;
  email: string;
}

interface Pagination {
  page: number;
  limit: number;
  total: number;
  total_pages: number;
}

const toTaipeiTime = (dateStr: string) => {
  const utcDateStr = dateStr.endsWith('Z') ? dateStr : dateStr + 'Z';
  const date = new Date(utcDateStr);
  return date.toLocaleString('zh-TW', { 
    timeZone: 'Asia/Taipei',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  });
};

const formatRequestParams = (params: string | Record<string, unknown> | null | undefined): string => {
  if (!params) return '{}';
  if (typeof params === 'string') {
    try {
      return JSON.stringify(JSON.parse(params), null, 2);
    } catch {
      return params;
    }
  }
  return JSON.stringify(params, null, 2);
};

export default function LogsPage() {
  const { user } = useAuth();
  const { addToast } = useToast();
  const [logs, setLogs] = useState<RequestLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedLog, setSelectedLog] = useState<LogDetailData | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [filters, setFilters] = useState({ startDate: '', endDate: '', status: '', username: '', requestSource: '' });
  const [pagination, setPagination] = useState<Pagination>({ page: 1, limit: 20, total: 0, total_pages: 0 });
  const [userSuggestions, setUserSuggestions] = useState<UserSuggestion[]>([]);
  const [showUsernameSuggestions, setShowUsernameSuggestions] = useState(false);
  const usernameRef = useRef<HTMLDivElement>(null);
  const isAdmin = user?.is_superuser;

  useEffect(() => { fetchLogs(); }, [pagination.page, pagination.limit]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (usernameRef.current && !usernameRef.current.contains(e.target as Node)) setShowUsernameSuggestions(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append('page', pagination.page.toString());
      params.append('limit', pagination.limit.toString());
      if (filters.startDate) params.append('start_date', filters.startDate);
      if (filters.endDate) params.append('end_date', filters.endDate);
      if (filters.status) params.append('status', filters.status);
      if (filters.username) params.append('username', filters.username);
      if (filters.requestSource) params.append('request_source', filters.requestSource);
      const response = await api.get<{ logs: RequestLog[]; pagination: Pagination }>('/api/v1/logs?' + params);
      setLogs(response.data.logs || []);
      if (response.data.pagination) setPagination(prev => ({ ...prev, ...response.data.pagination }));
    } catch { addToast('error', '無法載入日誌'); }
    finally { setLoading(false); }
  };

  const handleFilter = () => { setPagination(prev => ({ ...prev, page: 1 })); fetchLogs(); };

  const searchUsers = useCallback(async (query: string, field: 'username' | 'email') => {
    if (query.length < 1) { setUserSuggestions([]); return; }
    try {
      const response = await api.get<UserSuggestion[]>('/api/v1/users/search?' + field + '=' + encodeURIComponent(query));
      setUserSuggestions(response.data);
    } catch { setUserSuggestions([]); }
  }, []);

  const handleUsernameChange = (value: string) => { setFilters({ ...filters, username: value }); searchUsers(value, 'username'); setShowUsernameSuggestions(true); };
  const selectUsernameSuggestion = (s: UserSuggestion) => { setFilters({ ...filters, username: s.username }); setShowUsernameSuggestions(false); };

  const copyToClipboard = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      addToast('success', '已複製' + label);
    } catch {
      addToast('error', '複製失敗');
    }
  };

  const handleDelete = async () => {
    if (selectedIds.size === 0) return;
    try {
      await api.delete('/api/v1/logs', { data: { ids: Array.from(selectedIds) } });
      addToast('success', '已刪除 ' + selectedIds.size + ' 筆日誌');
      setSelectedIds(new Set()); setDeleteConfirm(false); fetchLogs();
    } catch (error: any) { addToast('error', error.response?.data?.error?.message || '刪除失敗'); }
  };

  const toggleSelect = (id: number) => { const newSet = new Set(selectedIds); if (newSet.has(id)) newSet.delete(id); else newSet.add(id); setSelectedIds(newSet); };
  const toggleSelectAll = () => { if (selectedIds.size === logs.length) setSelectedIds(new Set()); else setSelectedIds(new Set(logs.map(l => l.id))); };
  const formatRequestSource = (src: string | null | undefined) => src === 'playground' ? <span className="px-2 py-1 rounded text-xs bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">測試</span> : <span className="px-2 py-1 rounded text-xs bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">調用</span>;
  const formatTokens = (tokens: number | null | undefined) => !tokens ? '-' : tokens.toLocaleString();
  const goToPage = (page: number) => { if (page >= 1 && page <= pagination.total_pages) setPagination(prev => ({ ...prev, page })); };
  const changeLimit = (newLimit: number) => { setPagination(prev => ({ ...prev, limit: newLimit, page: 1 })); };

  const fetchLogDetail = async (logId: number) => {
    try {
      setLoadingDetail(true);
      const response = await api.get<LogDetailData>('/api/v1/logs/' + logId);
      setSelectedLog(response.data);
    } catch {
      addToast('error', '無法載入詳情');
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleShowDetail = (log: RequestLog) => {
    setSelectedLog(log as LogDetailData);
    fetchLogDetail(log.id);
  };

  const columns = [
    { key: 'select', header: (<input type="checkbox" checked={selectedIds.size === logs.length && logs.length > 0} onChange={toggleSelectAll} className="rounded" />), render: (log: RequestLog) => (<input type="checkbox" checked={selectedIds.has(log.id)} onChange={() => toggleSelect(log.id)} className="rounded" />) },
    { key: 'created_at', header: '時間', render: (log: RequestLog) => <span className="text-xs whitespace-nowrap">{toTaipeiTime(log.created_at)}</span> },
    ...(isAdmin ? [{ key: 'user_username', header: '用戶', render: (log: RequestLog) => log.user_username || '-' }] : []),
    { key: 'target_url', header: '目標網址', render: (log: RequestLog) => (<span className="truncate max-w-xs block" title={log.target_url}>{log.target_url}</span>) },
    { key: 'status_code', header: '狀態', render: (log: RequestLog) => (<span className={log.status_code && log.status_code < 400 ? 'px-2 py-1 rounded text-xs bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' : 'px-2 py-1 rounded text-xs bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'}>{log.status_code || '錯誤'}</span>) },
    { key: 'request_source', header: '類型', render: (log: RequestLog) => formatRequestSource(log.request_source) },
    { key: 'tokens_used', header: 'Tokens', render: (log: RequestLog) => formatTokens(log.tokens_used) },
    { key: 'duration_ms', header: '耗時', render: (log: RequestLog) => log.duration_ms ? log.duration_ms + 'ms' : '-' },
    ...(isAdmin ? [
      { key: 'proxy_address', header: '代理', render: (log: RequestLog) => log.proxy_address || '直連' },
      { key: 'traffic_bytes', header: '流量', render: (log: RequestLog) => log.traffic_bytes ? (log.traffic_bytes / 1024).toFixed(1) + ' KB' : '-' },
    ] : []),
    { key: 'actions', header: '操作', render: (log: RequestLog) => (<Button size="sm" variant="secondary" onClick={() => handleShowDetail(log)}>詳情</Button>) },
  ];

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">請求日誌</h1>
        {selectedIds.size > 0 && (<Button variant="danger" onClick={() => setDeleteConfirm(true)}>刪除選中 ({selectedIds.size})</Button>)}
      </div>
      <Card>
        <div className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-7 gap-4">
            <Input label="開始日期" type="date" value={filters.startDate} onChange={(e) => setFilters({ ...filters, startDate: e.target.value })} />
            <Input label="結束日期" type="date" value={filters.endDate} onChange={(e) => setFilters({ ...filters, endDate: e.target.value })} />
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">狀態</label>
              <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white">
                <option value="">全部</option><option value="success">成功</option><option value="error">失敗</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">類型</label>
              <select value={filters.requestSource} onChange={(e) => setFilters({ ...filters, requestSource: e.target.value })} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white">
                <option value="">全部</option><option value="playground">測試</option><option value="api">調用</option>
              </select>
            </div>
            {isAdmin && (
              <div className="relative" ref={usernameRef}>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">用戶名稱</label>
                <input type="text" value={filters.username} onChange={(e) => handleUsernameChange(e.target.value)} onFocus={() => filters.username && setShowUsernameSuggestions(true)} placeholder="輸入搜尋..." className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
                {showUsernameSuggestions && userSuggestions.length > 0 && (<ul className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg max-h-40 overflow-auto">{userSuggestions.map(s => (<li key={s.id} onClick={() => selectUsernameSuggestion(s)} className="px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer text-sm">{s.username}</li>))}</ul>)}
              </div>
            )}
            <div className="flex items-end"><Button onClick={handleFilter}>篩選</Button></div>
          </div>
        </div>
      </Card>
      <Card>
        {loading ? (<div className="flex items-center justify-center h-32"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div></div>) : (
          <>
            <Table data={logs} columns={columns} keyExtractor={(log) => log.id} emptyMessage="暫無日誌" />
            <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                <span>每頁顯示</span>
                <select value={pagination.limit} onChange={(e) => changeLimit(Number(e.target.value))} className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white">
                  <option value={10}>10</option><option value={20}>20</option><option value={50}>50</option><option value={100}>100</option>
                </select>
                <span>筆，共 {pagination.total} 筆</span>
              </div>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="secondary" onClick={() => goToPage(1)} disabled={pagination.page === 1}>首頁</Button>
                <Button size="sm" variant="secondary" onClick={() => goToPage(pagination.page - 1)} disabled={pagination.page === 1}>上一頁</Button>
                <span className="px-3 py-1 text-sm text-gray-600 dark:text-gray-400">第 {pagination.page} / {pagination.total_pages || 1} 頁</span>
                <Button size="sm" variant="secondary" onClick={() => goToPage(pagination.page + 1)} disabled={pagination.page >= pagination.total_pages}>下一頁</Button>
                <Button size="sm" variant="secondary" onClick={() => goToPage(pagination.total_pages)} disabled={pagination.page >= pagination.total_pages}>末頁</Button>
              </div>
            </div>
          </>
        )}
      </Card>
      <Modal isOpen={!!selectedLog} onClose={() => setSelectedLog(null)} title="請求詳情">
        {selectedLog && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div><span className="text-gray-500 dark:text-gray-400">時間：</span>{toTaipeiTime(selectedLog.created_at)}</div>
              <div><span className="text-gray-500 dark:text-gray-400">狀態碼：</span>{selectedLog.status_code || '無'}</div>
              <div><span className="text-gray-500 dark:text-gray-400">耗時：</span>{selectedLog.duration_ms ? selectedLog.duration_ms + 'ms' : '-'}</div>
              <div><span className="text-gray-500 dark:text-gray-400">類型：</span>{selectedLog.request_source === 'playground' ? '測試' : '調用'}</div>
              <div><span className="text-gray-500 dark:text-gray-400">Tokens：</span>{formatTokens(selectedLog.tokens_used)}</div>
              <div><span className="text-gray-500 dark:text-gray-400">流量：</span>{selectedLog.traffic_bytes ? (selectedLog.traffic_bytes / 1024).toFixed(1) + ' KB' : '-'}</div>
              {isAdmin && <div><span className="text-gray-500 dark:text-gray-400">用戶：</span>{selectedLog.user_username || '-'}</div>}
              {isAdmin && <div><span className="text-gray-500 dark:text-gray-400">代理：</span>{selectedLog.proxy_address || '直連'}</div>}
            </div>
            {isAdmin && (selectedLog.retry_sequence_display || selectedLog.retry_sequence) && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">重試路徑</label>
                <code className="block p-2 bg-gray-100 dark:bg-gray-700 rounded text-sm break-all">{selectedLog.retry_sequence_display || (Array.isArray(selectedLog.retry_sequence) ? selectedLog.retry_sequence.join(' -> ') : selectedLog.retry_sequence)}</code>
              </div>
            )}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">目標網址</label>
                <button onClick={() => copyToClipboard(selectedLog.target_url, '目標網址')} className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300" title="複製">複製</button>
              </div>
              <code className="block p-2 bg-gray-100 dark:bg-gray-700 rounded text-sm break-all">{selectedLog.target_url}</code>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">請求參數</label>
                <button onClick={() => copyToClipboard(formatRequestParams(selectedLog.request_params), '請求參數')} className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300" title="複製">複製</button>
              </div>
              <pre className="p-2 bg-gray-100 dark:bg-gray-700 rounded text-sm overflow-auto max-h-40 whitespace-pre-wrap break-all">{formatRequestParams(selectedLog.request_params)}</pre>
            </div>
            {selectedLog.error_message && (
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-sm font-medium text-red-600 dark:text-red-400">錯誤訊息</label>
                  {isAdmin && <button onClick={() => copyToClipboard(selectedLog.error_message!, '錯誤訊息')} className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300" title="複製">複製</button>}
                </div>
                <code className="block p-2 bg-red-50 dark:bg-red-900/20 rounded text-sm text-red-600 dark:text-red-400 break-all whitespace-pre-wrap">
                  {selectedLog.error_message}
                </code>
              </div>
            )}
            {loadingDetail ? (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">回應內容</label>
                <div className="p-4 bg-gray-100 dark:bg-gray-700 rounded flex items-center justify-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-600"></div>
                  <span className="ml-2 text-sm text-gray-500">載入中...</span>
                </div>
              </div>
            ) : selectedLog.response_content ? (
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">回應內容</label>
                  <button onClick={() => copyToClipboard(selectedLog.response_content!, '回應內容')} className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300" title="複製">複製</button>
                </div>
                <pre className="p-2 bg-gray-100 dark:bg-gray-700 rounded text-sm overflow-auto max-h-60 whitespace-pre-wrap break-all">{selectedLog.response_content}</pre>
              </div>
            ) : !selectedLog.error_message ? (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">回應內容</label>
                <p className="p-2 bg-gray-100 dark:bg-gray-700 rounded text-sm text-gray-500">無回應內容</p>
              </div>
            ) : null}
          </div>
        )}
      </Modal>
      <Modal isOpen={deleteConfirm} onClose={() => setDeleteConfirm(false)} title="確認刪除">
        <p className="text-gray-600 dark:text-gray-400 mb-4">確定要刪除選中的 {selectedIds.size} 筆日誌嗎？此操作無法復原。</p>
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={() => setDeleteConfirm(false)}>取消</Button>
          <Button variant="danger" onClick={handleDelete}>確認刪除</Button>
        </div>
      </Modal>
    </div>
  );
}
